import React, {
    ReactElement, useEffect,
} from "react";
import { IconType } from "react-icons";
import { AiFillForward, AiOutlineMail, AiOutlineMessage } from "react-icons/ai";
import { BiHash, BiRocket, BiTime } from "react-icons/bi";
import {
    BsAlarm, BsHandThumbsUp, BsShopWindow, BsShuffle,
} from "react-icons/bs";
import { FaWpforms } from "react-icons/fa";
import { FiColumns, FiExternalLink } from "react-icons/fi";
import { HiOutlineTemplate } from "react-icons/hi";
import { IoPeople, IoPerson } from "react-icons/io5";
import { MdEditNote } from "react-icons/md";
import { SiCodemagic, SiSlack } from "react-icons/si";
import { PiMicrosoftTeamsLogo } from "react-icons/pi";
import { VscSettingsGear } from "react-icons/vsc";
import { RiListSettingsFill, RiMoneyDollarBoxFill } from "react-icons/ri";
import { Loading } from "../../components";
import useRouting, { useParams } from "../../contexts/Routing";
import { Settings, useGetSettingsQuery } from "../../__generated__/api";
import { ErrorRemote } from "../error/Error";
import Styles from "./Administrator.module.scss";
import SlackSettings from "./sections/channels/slack/slack";
import MicrosoftTeamsSettings from "./sections/channels/microsoft-teams/microsoft-teams";

import ChannelTagSettings from "./sections/channels/channel-tags/ChannelTagSettings";

import SLASettings from "./sections/sla/SLA";

import Customization from "./sections/customization/Customization";
import Feedback from "./sections/feedback/Feedback";
import PortalSettings from "./sections/portal/Portal";
import ActionForm from "./sections/request-form/Actions";
import WebhookForm from "./sections/request-form/Webhooks";
import RequestTypes from "./sections/request-types/RequestTypes";
import TicketStates from "./sections/ticket-states/TicketStates";

import EmailSettings from "./sections/channels/email/Email";
import WebChatSettings from "./sections/channels/web-chat/WebChat";
import Forwarding from "./sections/channels/forwarding/Forwarding";
import CustomField from "./sections/custom-field/CustomField";
import QueueSettings from "./sections/queues/Queues";
import RequestForm from "./sections/request-form/RequestForm";

import ChatSettings from "./sections/chat/Chat";
import TimeSettings from "./sections/time/Time";
import UserGroupSettings from "./sections/user-groups/UserGroups";
import UsersSettings from "./sections/users/Users";
import WorkflowServicesSettings from "./sections/workflow-services/Services";
import WorkflowViewServiceSettings from "./sections/workflow-services/ViewService";

import ChannelInvitesSettings from "./sections/channel-invites/ChannelInvites";
import Home from "./sections/home/Home";

import ArtificialIntelligence from "./sections/artificial-intelligence/ArtificialIntelligence";
import Billing, { BillingSuccess } from "./sections/billing/Billing";

import AuditLogs from "./sections/audit-logs/AuditLogs";

import { SettingsProps } from "./sections/base";
import usePortal, { Entitlements } from "../../contexts/PortalContext";

interface Section {
    title?: string;
    name?: string;
    slug: string;
    page?: (props: SettingsProps) => ReactElement;
    url?: string;
    icon?: IconType;
    featureFlag?: string;
    entitlementId?: string;
}
interface Category {
    name: string;
    sections: Section[]
}

const SECTIONS: Category[] = [
    {
        name: "Support",
        sections: [
            {
                slug: "home",
                page: Home,
            },
            {
                title: "Settings",
                name: "Customize",
                slug: "customize",
                page: Customization,
                icon: MdEditNote,
            },
            {
                title: "Time Settings",
                name: "Time",
                slug: "time",
                page: TimeSettings,
                icon: BiTime,
            },

            {
                title: "Feedback",
                name: "Feedback",
                slug: "feedback",
                page: Feedback,
                icon: BsHandThumbsUp,
            },
            {
                title: "Request Types",
                name: "Request Types",
                slug: "request-types",
                page: RequestTypes,
                icon: FaWpforms,
            },
            {
                title: "Custom Field",
                slug: "request-fields",
                page: CustomField,
            },
            {
                title: "Request Type",
                slug: "request-forms",
                page: RequestForm,

            },
            {
                name: "Queues",
                title: "Queues",
                slug: "queues",
                page: QueueSettings,
                icon: BsShuffle,
            },
            {
                name: "SLA",
                title: "SLA",
                slug: "sla",
                page: SLASettings,
                icon: BsAlarm,
            },
            {
                name: "Columns",
                title: "Columns",
                slug: "ticket-states",
                page: TicketStates,
                icon: FiColumns,

            },
            {
                name: "AI",
                title: "Artificial Intelligence",
                slug: "ai",
                page: ArtificialIntelligence,
                icon: SiCodemagic,
            },

        ],
    },
    {
        name: "Channels",
        sections: [

            {
                title: "Channel Tag Settings",
                name: "Tags",
                slug: "channel-tags",
                page: ChannelTagSettings,
                icon: BiHash,
            },
            {
                title: "Slack Settings",
                name: "Slack",
                slug: "channels-slack",
                page: SlackSettings,
                icon: SiSlack,
            },
            {
                title: "Microsoft Teams Settings",
                name: "Microsoft Teams",
                slug: "channels-microsoft-teams",
                page: MicrosoftTeamsSettings,
                icon: PiMicrosoftTeamsLogo,
                featureFlag: "frontend.channels.microsoft-teams",
            },
            {
                title: "Chat",
                slug: "chat",
                page: ChatSettings,
            },

            {
                title: "Email Settings",
                name: "Email",
                slug: "channels-emails",
                page: EmailSettings,
                icon: AiOutlineMail,
            },
            {
                title: "Web Chat",
                name: "Web Chat",
                slug: "web-chat",
                page: WebChatSettings,
                icon: AiOutlineMessage,
            },
            {
                title: "Forwarding",
                name: "Forwarding",
                slug: "forwading",
                page: Forwarding,
                icon: AiFillForward,
            },

        ],
    },
    {
        name: "Users",
        sections: [
            {
                name: "Users",
                title: "Users",
                slug: "users",
                page: UsersSettings,
                icon: IoPerson,
            },
            {
                name: "Groups",
                title: "Groups",
                slug: "user-groups",
                page: UserGroupSettings,
                icon: IoPeople,
            },
        ],
    },
    {
        name: "Automation",
        sections: [
            {
                name: "Services",
                title: "Services",
                slug: "workflow-services",
                page: WorkflowServicesSettings,
                icon: BsShopWindow,
            },
            {
                title: "",
                slug: "workflow-service",
                page: WorkflowViewServiceSettings,
            },
            {
                name: "Actions",
                title: "Actions",
                slug: "workflow-actions",
                page: ActionForm,
                icon: BiRocket,
            },
            {

                title: "",
                slug: "workflow-webhooks",
                page: WebhookForm,
                icon: BiRocket,
            },

        ],
    },
    {
        name: "Portal",
        sections: [
            {
                title: "Portal Settings",
                name: "Settings",
                slug: "portal",
                page: PortalSettings,
                icon: VscSettingsGear,
            },
            {
                name: "Audit logs",
                slug: "audit-logs",
                page: AuditLogs,
                icon: RiListSettingsFill,
                entitlementId: Entitlements.KEYS.AUDIT_LOGS,
                featureFlag: "frontend.audit-logs.isEnabled",
            },
            {
                title: "Channel Templates",
                name: "Channel Templates",
                slug: "channel-invites",
                page: ChannelInvitesSettings,
                icon: HiOutlineTemplate,
            },

        ],
    },

    {
        name: "Billing",
        sections: [

            {
                slug: "billing",
                name: "Billing",
                page: Billing,
                title: "Set up Billing",
                icon: RiMoneyDollarBoxFill,
            },
            {
                slug: "billing-success",
                page: BillingSuccess,
            },

        ],
    },

];

const DEFAULT = SECTIONS[0].sections[0];
const FLAT_SECTIONS = SECTIONS.flatMap(row => row.sections);

export const FEATURE_DARKER = "frontend.design.darker";

export default function Administrator(): ReactElement {
    const { Paths, historyPush } = useRouting();
    const { featureFlags, entitlementIds, useTitle } = usePortal();
    const {
        data: settings, loading: isLoading, error, refetch,
    } = useGetSettingsQuery();
    let { setting } = useParams(Paths.ADMINISTRATOR_SETTING);
    const adminIdSettings = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    const isDarker = featureFlags.has(FEATURE_DARKER);

    if (!setting) {
        setting = adminIdSettings.setting;
        if (!setting) {
            const defaultItem = FLAT_SECTIONS.find(row => row === DEFAULT);
            if (defaultItem) {
                historyPush(Paths.ADMINISTRATOR_SETTING, { setting: defaultItem.slug });
            }
        }
    }

    const container = setting ? FLAT_SECTIONS.find(page => page.slug === setting) : undefined;
    const Page = container ? container.page : undefined;

    useTitle(container?.title ?? "");

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }
    return (

        <div className="d-flex justify-content-between align-items-stretch h-100" data-bs-theme="default">
            <div className={`overflow-hidden flex-shrink-0 mb-5 ${Styles.sidebar}`}>
                <div className="position-sticky">
                    <div className="text-center pt-3 pb-2">
                        <h4 className="text-gray-500">Settings</h4>
                    </div>
                    {SECTIONS.map(group => (
                        <div key={group.name}>
                            {isDarker ? (
                                <div className="mt-3 ms-2 text-gray-600 mb-2 fw-bolder">
                                    {group.name}
                                </div>
                            ) : (
                                <h5 className="mt-3 ms-2">
                                    {group.name}
                                </h5>
                            )}

                            <div className="" role="tablist">
                                {group.sections
                                    .filter(section => section.name
                                        && (section.featureFlag ? featureFlags.has(section.featureFlag) : true)
                                        && (section.entitlementId ? entitlementIds.has(section.entitlementId) : true))
                                    .map(section => {
                                        const Icon = section.icon;
                                        const isSelected = container?.slug === section.slug;
                                        return (
                                            (section.url ? (
                                                <a
                                                    key={section.slug}
                                                    className={`mt-2 border-start d-flex text-decoration-none `
                                                    + ` list-border`
                                                    + ` rounded-0  align-items-center my-2 py-0 text-default`}
                                                    href={section.url}
                                                    rel="noreferrer"
                                                    target="_blank"
                                                >
                                                    <FiExternalLink className="text-light me-2" />
                                                    {section.name}
                                                </a>
                                            ) : (
                                                <button
                                                    key={section.slug}
                                                    type="button"
                                                    className={`list-group-item list-group-item-action border-start d-flex `
                                                    + ` list-border ${isSelected
                                                        ? "fw-semibold list-border-primary " : ""}`
                                                    + ` rounded-0  align-items-center mb-1`}
                                                    onClick={() => {
                                                        refetch();
                                                        historyPush(Paths.ADMINISTRATOR_SETTING, { setting: section.slug });
                                                    }}
                                                >
                                                    {Icon && <Icon className={`me-2 ${!isSelected ? "text-light" : ""}`} />}
                                                    {section.name}
                                                </button>
                                            ))

                                        );
                                    })}
                            </div>
                        </div>
                    ))}
                </div>

            </div>

            <div className="flex-grow-1 p-3 d-flex flex-column shadow">
                {container?.title && (
                    <h5 className="mb-4">
                        {container?.title}
                    </h5>
                )}

                {Page ? <Page isV3={false} settings={settings?.settings as Settings} /> : undefined}

            </div>

        </div>

    );
}
